<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">
        <i :class="'fa fa-fw fa-' + (allowExisting ? 'user' : 'envelope')"></i>
        {{ allowExisting ? _('Add a user...') : _('Invite user') }}
      </h4>
    </div>
    <div class="modal-body">
      <p v-if="contextLabel" class="text-muted" style="margin-top: 0;">{{ contextLabel }}</p>
      <div class="row" v-if="showAddGroupSelect">
        <div class="col-md-12">
          <div class="form-group">
            <label>{{ _('User group') }}</label>
            <select class="form-control" v-model="addGroupId">
              <option :value="null">{{ _('Select a user group...') }}</option>
              <option v-for="group in selectableGroups" :key="'add-' + group.id" :value="parseInt(group.id)">{{ group.name }}</option>
            </select>
          </div>
        </div>
      </div>
      <div v-if="showAddPath" class="invite-path">
        <div class="invite-path-title">{{ _('Add a user...') }}</div>
        <div class="form-group" style="margin-bottom: 8px;">
          <v-select
            v-model="selectedUserToAdd"
            :options="userHits"
            :filterable="false"
            :clearable="true"
            label="label"
            :disabled="!canPickExisting"
            :placeholder="_('Type to search users')"
            v-on:search="onUserSearch"
          >
            <template v-slot:no-options="{ searching }">
              <template v-if="searching">{{ _('No results found.') }}</template>
              <template v-else>{{ _('Type to search users') }}</template>
            </template>
          </v-select>
        </div>
      </div>
      <div v-if="showPathOr" class="invite-path-or"><span>{{ _('or') }}</span></div>
      <div v-if="showInvitePath" :class="allowExisting ? 'invite-path' : ''">
        <div v-if="allowExisting" class="invite-path-title">{{ _('Invite user') }}</div>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>{{ _('Email') }} <span class="text-danger">*</span></label>
              <input type="email" class="form-control" v-model="email" :placeholder="_('user@example.com')" autofocus />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>{{ _('Name') }} <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="name" :placeholder="_('Full name')" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="showSharedDates" class="invite-dates">
        <div class="row">
          <div class="col-sm-6">
            <div class="form-group">
              <DatePicker
                class="invite-datepicker"
                v-model="startdate"
                :shortLocale="shortLocale"
                :title="_('Start date')"
                :placeholder="_('No start date')"
                :staticInput="true"
                :clearable="true"
                :max-date="enddate"
                field="invitestart"
              ></DatePicker>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="form-group">
              <DatePicker
                class="invite-datepicker"
                v-model="enddate"
                :shortLocale="shortLocale"
                :title="_('End date')"
                :placeholder="_('No end date')"
                :staticInput="true"
                :clearable="true"
                :min-date="startdate"
                field="inviteend"
              ></DatePicker>
            </div>
          </div>
        </div>
        <p v-if="isInviting || !allowExisting" class="help-block">{{ _('Optional. Leave empty for unlimited access. Dates apply when the invitation is accepted.') }}</p>
      </div>
      <div class="row" v-if="showGroupSelector && groups.length">
        <div class="col-md-12">
          <div class="form-group">
            <label>{{ advancedCollections ? _('User groups') : _('Collections') }}</label>
            <div class="group-selector" style="border: 1px solid #ccc; border-radius: 4px; max-height: 200px; overflow-y: auto; padding: 6px 0;">
              <label
                v-for="group in selectableGroups"
                :key="group.id"
                class="group-selector-item"
                style="display: flex; align-items: center; padding: 5px 12px; cursor: pointer; margin: 0;"
                :style="{ backgroundColor: selectedGroupIds.includes(group.id) ? '#e8f4fd' : '' }"
              >
                <input
                  type="checkbox"
                  :value="group.id"
                  v-model="selectedGroupIds"
                  style="margin-right: 8px; cursor: pointer;"
                />
                <span>{{ group.name }}</span>
                <small v-if="group.description" class="text-muted" style="margin-left: 6px;">— {{ group.description }}</small>
              </label>
            </div>
            <small v-if="!groups.length && !loadingGroups" class="text-muted">
              {{ advancedCollections ? _('No user groups found.') : _('No collections found.') }}
            </small>
            <small v-if="loadingGroups" class="text-muted">
              <i class="fa fa-spinner fa-spin"></i> {{ _('Loading...') }}
            </small>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Cancel') }}</button>
      <button class="btn btn-success" v-on:click="send" :disabled="!canSend">
        <i :class="'fa fa-fw fa-' + sendButtonIcon"></i> {{ sendButtonLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import debounce from 'lodash/debounce'
import { DateTime } from 'luxon'
import errors from '~/lib/errors'
import { isEmailAmongMembers, isEmailMemberOfGroups } from '~/lib/inviteMembers'

export default {
  mixins: [errors],
  props: {
    baseDomain: { type: String, required: true },
    shopId: { type: String, required: true },
    apiKey: { type: String, required: true },
    locale: { type: String, required: true },
    advancedCollections: { default: 0 },
    groupsUrl: { type: String, default: '' },
    presetGroupIds: { type: Array, default: () => [] },
    queueOnly: { type: Boolean, default: false },
    contextLabel: { type: String, default: '' },
    hideGroupSelector: { type: Boolean, default: false },
    memberEmails: { type: Array, default: () => [] },
    availableGroups: { type: Array, default: null },
    allowExisting: { type: Boolean, default: false },
    usersUrl: { type: String, default: '' },
    excludeGroupIds: { type: Array, default: () => [] }
  },
  data () {
    const _ = useNuxtApp().$translate
    return {
      _,
      email: '',
      name: '',
      startdate: null,
      enddate: null,
      selectedGroupIds: [],
      addGroupId: null,
      selectedUserToAdd: null,
      userHits: [],
      groups: [],
      loadingGroups: false,
      sending: false,
      sent: false
    }
  },
  computed: {
    shortLocale () {
      return this.locale ? this.locale.split('_')[0] : ''
    },
    showGroupSelector () {
      return !this.hideGroupSelector && !this.queueOnly && !this.allowExisting
    },
    showAddGroupSelect () {
      return this.allowExisting && this.selectableGroups.length > 1
    },
    selectableGroups () {
      return this.groups.filter(g => !parseInt(g.allusers))
    },
    addTargetGroupId () {
      if (this.addGroupId) return parseInt(this.addGroupId)
      if (this.presetGroupIds && this.presetGroupIds.length === 1) {
        return parseInt(this.presetGroupIds[0])
      }
      if (this.selectableGroups.length === 1) {
        return parseInt(this.selectableGroups[0].id)
      }
      return null
    },
    canPickExisting () {
      return this.allowExisting && !!this.addTargetGroupId && !this.sending
    },
    isAddingExisting () {
      return !!(this.selectedUserToAdd && this.selectedUserToAdd.id)
    },
    isInviting () {
      return !!(String(this.email || '').trim() || String(this.name || '').trim())
    },
    showAddPath () {
      return this.allowExisting && !this.isInviting
    },
    showInvitePath () {
      return !this.allowExisting || !this.isAddingExisting
    },
    showPathOr () {
      return this.allowExisting && !this.isAddingExisting && !this.isInviting
    },
    showSharedDates () {
      return !this.allowExisting || this.isAddingExisting || this.isInviting
    },
    effectiveGroupIds () {
      if (this.addTargetGroupId) {
        return [this.addTargetGroupId]
      }
      if (this.presetGroupIds && this.presetGroupIds.length) {
        return this.presetGroupIds.map(id => parseInt(id)).filter(id => id > 0)
      }
      return this.selectedGroupIds
    },
    canSend () {
      if (this.sending || this.sent) return false
      if (this.isAddingExisting) {
        return !!this.addTargetGroupId
      }
      return !!this.email && !!this.name.trim()
    },
    sendButtonIcon () {
      if (this.sending) return 'cog fa-spin'
      if (this.sent) return 'check'
      if (this.isAddingExisting) return 'plus'
      return this.queueOnly ? 'plus' : 'send'
    },
    sendButtonLabel () {
      if (this.sending) {
        return this.isAddingExisting ? this._('Processing') + '...' : this._('Sending...')
      }
      if (this.sent) return this.queueOnly ? this._('Queued!') : this._('Sent!')
      if (this.isAddingExisting) return this._('Add a user...')
      return this.queueOnly ? this._('Add invitation') : this._('Send invitation')
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    isAlreadyMember (user, excludeGroupIds) {
      if (!user || !excludeGroupIds.length) return false
      const ids = Array.isArray(user.groupids)
        ? user.groupids.map(id => parseInt(id, 10))
        : []
      return excludeGroupIds.some(groupId => ids.includes(parseInt(groupId, 10)))
    },
    onUserSearch (search, loading) {
      loading(true)
      this.findUsers(loading, search)
    },
    findUsers: debounce(async function (loading, search) {
      if (!this.usersUrl || !search || String(search).trim().length < 2) {
        this.userHits = []
        loading(false)
        return
      }
      try {
        const response = await axios.get(this.usersUrl, {
          params: {
            sSearch: String(search).trim(),
            page: 1,
            size: 20
          }
        })
        const rows = Array.isArray(response.data) ? response.data : (response.data.data || [])
        const exclude = this.excludeGroupIds || []
        this.userHits = rows
          .filter(user => !this.isAlreadyMember(user, exclude))
          .map(user => ({
            ...user,
            label: (user.name || user.email) + (user.name && user.email ? ' — ' + user.email : '')
          }))
      } catch (e) {
        this.userHits = []
      }
      loading(false)
    }, 350),
    async addExistingUser () {
      const user = this.selectedUserToAdd
      if (!user || !user.id) return
      const groupId = this.addTargetGroupId
      if (!groupId) {
        return this.showError(this._('Select a user group...'))
      }
      const dates = this.membershipDates()
      if (!dates) return
      this.sending = true
      try {
        await axios.patch(
          this.baseDomain + this.shopId + '/usergroups/' + groupId + '.json?apikey=' + this.apiKey,
          {
            id: groupId,
            addMembers: [{
              id: parseInt(user.id),
              startdate: dates.startdate,
              enddate: dates.enddate
            }]
          }
        )
        this.$emit('added', user)
        this.close()
      } catch (e) {
        this.showError(e)
      } finally {
        this.sending = false
      }
    },
    toMembershipDate (value) {
      if (!value) return null
      if (value instanceof Date) {
        return DateTime.fromJSDate(value).toISODate() || null
      }
      if (typeof value === 'string' && value.trim()) {
        return value.trim().slice(0, 10)
      }
      return null
    },
    membershipDates () {
      const startdate = this.toMembershipDate(this.startdate)
      const enddate = this.toMembershipDate(this.enddate)
      if (startdate && enddate && enddate < startdate) {
        this.showError(this._('The end date cannot be before the start date.'))
        return null
      }
      return { startdate, enddate }
    },
    async send () {
      if (this.isAddingExisting) {
        return this.addExistingUser()
      }
      if (!this.email || !this.name.trim()) return
      const dates = this.membershipDates()
      if (!dates) return
      this.sending = true
      try {
        const payload = {
          email: this.email.trim(),
          name: this.name.trim(),
          startdate: dates.startdate,
          enddate: dates.enddate
        }
        if (this.queueOnly) {
          this.$emit('queued', payload)
          this.sent = true
          setTimeout(() => this.close(), 600)
          return
        }
        if (this.effectiveGroupIds.length) {
          const knownMembers = (this.memberEmails || []).map(email => ({ email }))
          const alreadyMember = knownMembers.length
            ? isEmailAmongMembers(payload.email, knownMembers)
            : await isEmailMemberOfGroups(
              { baseDomain: this.baseDomain, shopId: this.shopId, apiKey: this.apiKey },
              payload.email,
              this.effectiveGroupIds
            )
          if (alreadyMember) {
            this.showError(this._('This user is already a member.'))
            return
          }
        }
        const response = await axios.post(
          this.baseDomain + this.shopId + '/userinvitations.json?apikey=' + this.apiKey,
          {
            email: payload.email,
            name: payload.name,
            groupIds: this.effectiveGroupIds,
            locale: this.locale,
            startdate: payload.startdate,
            enddate: payload.enddate
          }
        )
        this.sent = true
        if (response.data && response.data.added) {
          this.showNotification(this._('%s already has an account and was added as a member.').replace('%s', payload.email))
          this.$emit('added', response.data)
        } else {
          this.showNotification(this._('Invitation sent to %s').replace('%s', payload.email))
        }
        setTimeout(() => this.close(), 1200)
      } catch (e) {
        this.showError(e)
      } finally {
        this.sending = false
      }
    },
    async loadGroups () {
      if (this.availableGroups && this.availableGroups.length) {
        this.groups = this.availableGroups
        this.ensureAddGroupId()
        return
      }
      if (!this.groupsUrl || !this.showGroupSelector) {
        return
      }
      this.loadingGroups = true
      try {
        const response = await axios.get(this.groupsUrl)
        this.groups = response.data || []
        this.ensureAddGroupId()
      } catch (e) {
        this.groups = []
      } finally {
        this.loadingGroups = false
      }
    },
    ensureAddGroupId () {
      if (this.addGroupId) return
      if (this.selectableGroups.length === 1) {
        this.addGroupId = parseInt(this.selectableGroups[0].id)
      }
    }
  },
  created () {
    if (this.presetGroupIds && this.presetGroupIds.length) {
      this.selectedGroupIds = [...this.presetGroupIds]
      if (this.presetGroupIds.length === 1) {
        this.addGroupId = parseInt(this.presetGroupIds[0])
      }
    }
    this.loadGroups()
  },
  watch: {
    selectedUserToAdd (user) {
      if (user && user.id) {
        this.email = ''
        this.name = ''
      }
    },
    email (value) {
      if (String(value || '').trim()) {
        this.selectedUserToAdd = null
      }
    },
    name (value) {
      if (String(value || '').trim()) {
        this.selectedUserToAdd = null
      }
    }
  }
}
</script>

<style scoped>
.invite-datepicker {
  display: block;
  width: 100%;
}
.invite-datepicker :deep(.dp__main),
.invite-datepicker :deep(.dp__input_wrap),
.invite-datepicker :deep(.dp__input),
.invite-datepicker :deep(.form-control) {
  width: 100%;
  max-width: none;
}
.invite-path {
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px 14px 6px;
}
.invite-path-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: #333;
}
.invite-path-or {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
  color: #888;
  font-size: 13px;
  font-weight: 600;
}
.invite-path-or::before,
.invite-path-or::after {
  content: '';
  flex: 1;
  border-top: 1px solid #ccc;
}
.invite-dates {
  margin-top: 12px;
}
</style>
